"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Mail, Clock, LogIn, User, LogOut } from "lucide-react";
import Link from "next/link";

// ── Flag SVG components ────────────────────────────────────────────────────

const FlagUK = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" className="rounded-sm flex-shrink-0">
    <rect width="20" height="14" fill="#012169" />
    <path d="M0 0L20 14M20 0L0 14" stroke="#fff" strokeWidth="3" />
    <path d="M0 0L20 14M20 0L0 14" stroke="#C8102E" strokeWidth="1.8" />
    <rect x="8.5" y="0" width="3" height="14" fill="#fff" />
    <rect x="0" y="5.5" width="20" height="3" fill="#fff" />
    <rect x="9.1" y="0" width="1.8" height="14" fill="#C8102E" />
    <rect x="0" y="6.1" width="20" height="1.8" fill="#C8102E" />
  </svg>
);

const FlagTH = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" className="rounded-sm flex-shrink-0">
    <rect width="20" height="14" fill="#A51931" />
    <rect y="2.33" width="20" height="2.33" fill="#fff" />
    <rect y="4.67" width="20" height="4.67" fill="#2D2A4A" />
    <rect y="9.33" width="20" height="2.33" fill="#fff" />
  </svg>
);

const FlagKR = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" className="rounded-sm flex-shrink-0">
    <rect width="20" height="14" fill="#fff" />
    <circle cx="10" cy="7" r="3.5" fill="#CD2E3A" />
    <path d="M10 3.5 A3.5 3.5 0 0 1 10 10.5 A1.75 1.75 0 0 1 10 7 A1.75 1.75 0 0 0 10 3.5Z" fill="#0047A0" />
    <line x1="4" y1="3.5" x2="7" y2="8.5" stroke="#1a1a1a" strokeWidth="0.7" />
    <line x1="5" y1="2.5" x2="8" y2="7.5" stroke="#1a1a1a" strokeWidth="0.7" />
    <line x1="13" y1="5.5" x2="16" y2="10.5" stroke="#1a1a1a" strokeWidth="0.7" />
    <line x1="12" y1="6.5" x2="15" y2="11.5" stroke="#1a1a1a" strokeWidth="0.7" />
  </svg>
);

// ── Language config ────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: "en", label: "English",   nativeLabel: "English",   Flag: FlagUK },
  { code: "th", label: "Thai",      nativeLabel: "ภาษาไทย",   Flag: FlagTH },
  { code: "kr", label: "Korean",    nativeLabel: "한국어",     Flag: FlagKR },
];

// ── LanguageSelector component ─────────────────────────────────────────────

export function LanguageSelector({ value, onChange, variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = LANGUAGES.find((l) => l.code === value) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDark = variant === "dark";

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold
          border transition-all duration-150 cursor-pointer
          ${isDark
            ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
            : "bg-white border-gray-200 text-gray-700 hover:border-red-400"
          }
        `}
      >
        <selected.Flag />
        <span className="hidden sm:inline tracking-wide">{selected.label}</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50">
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === value;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => { onChange(lang.code); setOpen(false); }}
                className={`
                  w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left
                  transition-colors duration-100 cursor-pointer
                  ${isActive
                    ? "bg-red-50 text-red-800"
                    : "text-gray-700 hover:bg-gray-50"
                  }
                `}
              >
                <lang.Flag />
                <span className="flex-1 font-medium">{lang.nativeLabel}</span>
                {isActive && <Check className="w-3.5 h-3.5 text-red-700 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── UserMenu component (login link or user dropdown) ──────────────────────

function UserMenu({ currentUser, getUserInitials, handleLogout, router, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!currentUser) {
    return (
      <Link
        href="/login"
        title="Login"
        className="flex items-center justify-center w-7 h-7 rounded-md
                   bg-white/10 border border-white/20 text-white
                   hover:bg-white/20 transition-all duration-150"
      >
        <LogIn className="w-3.5 h-3.5" />
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold
                   border bg-white/10 border-white/20 text-white hover:bg-white/20
                   transition-all duration-150 cursor-pointer"
      >
        <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center
                        text-white text-[10px] font-bold leading-none flex-shrink-0">
          {getUserInitials()}
        </div>
        <span className="hidden sm:inline tracking-wide">{t("Hi")}, {getUserInitials()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-48 bg-white rounded-lg
                        shadow-xl border border-gray-100 overflow-hidden z-[100]">
          <p className="px-4 py-2.5 text-xs font-semibold text-gray-500 border-b border-gray-100 truncate">
            {currentUser.full_name}
          </p>
          <button
            onClick={() => { router.push("/dashboard/settings"); setOpen(false); }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
            {t("My Profile")}
          </button>
          <button
            onClick={() => { handleLogout(); setOpen(false); }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {t("Logout")}
          </button>
        </div>
      )}
    </div>
  );
}

// ── TopBar component ───────────────────────────────────────────────────────

export function TopBar({ language, setLanguage, currentUser, getUserInitials, handleLogout, router, t }) {
  return (
    <div className="relative z-[60] w-full bg-red-800 py-2 px-6 sm:px-8 flex items-center justify-between gap-4">

      {/* Right — contact info + language + user */}
      <div className="flex items-center gap-4 ml-auto">
        <div className="hidden sm:flex items-center gap-1.5 text-white/85 text-xs font-medium tracking-wide">
        <Mail className="w-3.5 h-3.5 flex-shrink-0" />
        <span>support@duglobal.com</span>
        </div>

        <div className="hidden sm:block w-px h-3.5 bg-white/25" />

        <div className="hidden sm:flex items-center gap-1.5 text-white/85 text-xs font-medium tracking-wide">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Mon – Fri: 9:00 – 18:30</span>
        </div>

        <div className="hidden sm:flex w-px h-3.5 bg-white/25" />

        <LanguageSelector value={language} onChange={setLanguage} variant="dark" />

        <div className="w-px h-3.5 bg-white/25" />

        <UserMenu
          currentUser={currentUser}
          getUserInitials={getUserInitials}
          handleLogout={handleLogout}
          router={router}
          t={t}
        />
      </div>
    </div>
  );
}